import React from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import step1 from '../../../Assets/document/nodeExpo1.gif';
import step2 from '../../../Assets/document/nodeExpo2.gif';
import step3 from '../../../Assets/document/nodeExpo3.gif';
import step4 from '../../../Assets/document/nodeExpo4.gif';

const listItemStyle = {
  marginBottom: '0.75rem',
};

const CodeBlock = ({ code }) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
  };

  return (
    <Box sx={{ position: 'relative', mb: 2 }}>
      <Box
        component="pre"
        sx={{
          backgroundColor: '#f4f4f4',
          p: 2,
          borderRadius: 1,
          overflowX: 'auto',
          maxWidth: '100%',
          width: '100%',
          whiteSpace: 'pre-wrap', // wraps long lines
          wordBreak: 'break-word', // breaks long words
          fontSize: '0.875rem',
        }}
      >
        {code}
      </Box>
      <Tooltip title="Copy to clipboard">
        <IconButton
          onClick={handleCopy}
          aria-label="Copy command to clipboard"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'text.secondary',
          }}
          size="small"
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};


const NodeExporterDockerSetup = () => {
  return (
    <Box sx={{ px: { xs: 2, sm: 4 }, py: 2 }}>
      <Typography variant="h4" gutterBottom>Objective</Typography>
      <Typography paragraph>
        This guide will help you install and run the Prometheus Node Exporter inside a Docker container to monitor system metrics on a Linux host, including CPU, memory, disk, and network usage.
      </Typography>

      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>Prerequisites</Typography>
      <Typography paragraph>
        Before you begin, ensure you have Docker installed on your server. If not, visit the{' '}
        <a href="https://docs.docker.com/get-docker/" target="_blank" rel="noopener noreferrer">
          Docker Official Installation Guide
        </a>.
      </Typography>

      {/* Video Tutorial */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Watch the Setup Video
      </Typography>
      <Box display="flex" justifyContent="center" my={2}>
        <iframe
          width="100%"
          height="550"
          src="https://www.youtube.com/embed/VmZviiaRPck"
          title="Node Exporter Setup Video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{  borderRadius: '8px' }}
        />
      </Box>


      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>Step 1: Pull the Node Exporter Docker Image</Typography>
      <Typography paragraph>Open your terminal and run the following command:</Typography>
      <CodeBlock code="docker pull prom/node-exporter" />
      <Typography paragraph>This command downloads the official Node Exporter image from Docker Hub.</Typography>

      <Box display="flex" justifyContent="center" my={2}>
        <img
          src={step1}
          alt="Installation in progress"
          style={{
            maxWidth: '100%',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}
        />
      </Box>

      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>Step 2: Run the Node Exporter Container with Host Access</Typography>
      <Typography paragraph>By executing the following command:</Typography>
      <CodeBlock code={`docker run -d --name node-exporter --restart unless-stopped --net="host" --pid="host" -v "/:/host:ro,rslave" prom/node-exporter --path.rootfs=/host`} />

      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Explanation:</Typography>
      <ul style={{ paddingLeft: '1.25rem' }}>
        <li style={listItemStyle}><Typography variant="body1"><code>-d</code>: Run in detached mode (background).</Typography></li>
        <li style={listItemStyle}><Typography variant="body1"><code>--name node-exporter</code>: Container name</Typography></li>
        <li style={listItemStyle}><Typography variant="body1"><code>--restart unless-stopped</code>: Auto-restart unless explicitly stopped</Typography></li>
        <li style={listItemStyle}><Typography variant="body1"><code>--net=host</code>: Share the host network namespace</Typography></li>
        <li style={listItemStyle}><Typography variant="body1"><code>--pid=host</code>: Share the host's process namespace</Typography></li>
        <li style={listItemStyle}><Typography variant="body1"><code>-v /:/host:ro,rslave</code>: Mount the host filesystem as read-only</Typography></li>
        <li style={listItemStyle}><Typography variant="body1"><code>--path.rootfs=/host</code>: Point Node Exporter to the real host filesystem</Typography></li>
      </ul>
      <Box display="flex" justifyContent="center" my={2}>
        <img
          src={step2}
          alt="Installation in progress"
          style={{
            maxWidth: '100%',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}
        />
      </Box>

      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>Step 3: Verify the Container is Running</Typography>
      <Typography paragraph>Now, let’s confirm that the container is running by running:</Typography>
      <CodeBlock code="docker ps" />
      <Typography paragraph>
        You should see one called <code>node-exporter</code>, along with its container ID. If it’s listed, it’s running correctly.
      </Typography>

      <Box display="flex" justifyContent="center" my={2}>
        <img
          src={step3}
          alt="Installation in progress"
          style={{
            maxWidth: '100%',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}
        />
      </Box>

      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>Step 4: Verify Exported Metrics Output</Typography>
      <Typography paragraph>
        With the container running, the metrics are now available on port <code>9100</code>. Use the following command:
      </Typography>
      <CodeBlock code="curl http://localhost:9100/metrics" />
      <Typography paragraph>
        <strong>What You Should See:</strong> Text output with metrics starting with <code># HELP</code> and <code>node_</code> prefixes.
      </Typography>

      <Box display="flex" justifyContent="center" my={2}>
        <img
          src={step4}
          alt="Installation in progress"
          style={{
            maxWidth: '100%',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}
        />
      </Box>

      <Typography paragraph>
        <strong>Note:</strong> Make sure to open the Node Exporter Port: (<strong>9100</strong> watchnod ip <strong>:13.232.108.253</strong>), on your server security group/Firewall.
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>🎉 Great job!</Typography>
      <Typography paragraph>
        You've successfully installed the Node Exporter using Docker.
      </Typography>
    </Box>
  );
};

export default NodeExporterDockerSetup;
